import {
  Alert,
  Badge,
  Button,
  ConfirmDialog,
  DateInput,
  EmptyState,
  FieldBlock,
  Input,
  LoadingState,
  Modal,
  Select,
  Textarea,
  useToast,
} from '@orbind/ui'
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router'
import {
  ADMIN_SECTIONS,
  AUDIT_AUTOMACAO,
  findAdminSection,
  type AdminSection,
  type CatalogRow,
  type CompanyRow,
  type MemberRow,
} from '../mocks/admin.ts'
import { CatalogIcon, IconField } from '../components/IconPicker.tsx'
import {
  AccessMembersSection,
  AccessGroupsSection,
  AccessProfilesSection,
} from '../features/admin/AccessControl.tsx'
import { AreasSection } from '../features/admin/Areas.tsx'
import { CompaniesSection } from '../features/admin/Companies.tsx'
import { FeatureTogglesSection } from '../features/admin/FeatureToggles.tsx'
import { TemplatesSection } from '../features/admin/Templates.tsx'
import { CamposComplementaresSection } from '../features/admin/CamposComplementares.tsx'
import { PluginsSection } from '../features/admin/Plugins.tsx'
import { ConectoresDeIaSection } from '../features/admin/ConectoresDeIa.tsx'
import { HerdarResponsavelSection } from '../features/admin/HerdarResponsavel.tsx'
import { useSession } from '../lib/session.tsx'
import { FlowsSection } from '../features/admin/Flows.tsx'
import { PlansSection } from '../features/admin/Plans.tsx'
import { PrioritiesSection } from '../features/admin/Priorities.tsx'
import { RelationTypesSection } from '../features/admin/RelationTypes.tsx'
import { StatusesSection } from '../features/admin/Statuses.tsx'
import { WorkItemTypesSection } from '../features/admin/WorkItemTypes.tsx'
import { AdminSectionBlocked, useModuleGates } from '../lib/module-gate.tsx'

import { ExplorerBody, useExplorer, useExplorerConfig } from '../shell/explorer.tsx'
import './admin.css'

/**
 * As secoes reais (catalogos, acesso, toggles, planos) vivem em
 * `features/admin/*` e sao interceptadas por id em `conteudo()`. So idiomas,
 * integracoes e auditoria seguem com os dados de exemplo de `mocks/admin.ts`.
 */

/** "Correção rápida" → "CORRECAO_RAPIDA": sem acento, sem espaco, maiusculo. */
function normalizarCodigo(texto: string) {
  return texto
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/[^A-Za-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toUpperCase()
}

/**
 * Administração — cadastros da instalação.
 *
 * O explorer NAO pertence mais a esta tela: ele mora no shell
 * (`shell/AppShell.tsx`), como no protótipo, e a lista de cadastros sobe por
 * `ExplorerBody`. Aqui fica so o conteudo do `<main>`, que muda de forma
 * conforme a secao escolhida — os cinco modos de `showAdmin.html`
 * (membros, empresas, catalogo, auditoria e o cartao "ainda nao
 * detalhado").
 *
 * Dados MOCADOS: criar, editar e excluir sao apenas visuais. Alternar
 * situacao, notificacao de e-mail e acrescentar dominio mexem em estado local
 * para a tela responder de verdade, mas nada e gravado.
 */

/** Um membro especifico da uniao de secoes, pelo discriminante. */
type Secao<K extends AdminSection['kind']> = Extract<AdminSection, { kind: K }>

/** Paleta oferecida nos campos de cor dos cadastros (mesma do protótipo). */
const COLOR_CHOICES: readonly { readonly value: string; readonly label: string }[] = [
  { value: '#1B4DF5', label: 'Azul' },
  { value: '#0E9AE0', label: 'Azul-claro' },
  { value: '#19B27B', label: 'Verde' },
  { value: '#E0A106', label: 'Âmbar' },
  { value: '#D93A3A', label: 'Vermelho' },
  { value: '#6C5CE7', label: 'Roxo' },
  { value: '#E07A5F', label: 'Terracota' },
  { value: '#8A93B8', label: 'Cinza-azulado' },
]

type SituationFilter = 'all' | 'active' | 'inactive'

const SITUATION_OPTIONS = [
  { value: 'all' as const, label: 'Todas as situações' },
  { value: 'active' as const, label: 'Somente ativos' },
  { value: 'inactive' as const, label: 'Somente inativos' },
]

/** Registro sendo criado (id nulo) ou editado. */
type Editando = { readonly id: string | null; readonly nome: string }

/**
 * Interruptor ativo/inativo (protótipo `showAdmin.html:87`).
 *
 * `role="switch"` + `aria-checked` deixam o estado explicito para leitor de
 * tela; a posicao do botao e o rotulo ao lado cuidam de quem enxerga — a cor
 * nunca e o unico sinal.
 */
function Interruptor({
  ligado,
  rotulo,
  titulo,
  aoAlternar,
}: {
  ligado: boolean
  rotulo: string
  titulo: string
  aoAlternar: () => void
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={ligado}
      aria-label={rotulo}
      title={titulo}
      className={`adm-switch${ligado ? ' adm-switch--on' : ''}`}
      onClick={aoAlternar}
    >
      <span className="adm-switch__knob" aria-hidden />
    </button>
  )
}

/** Iniciais para o avatar da auditoria (o protótipo faz o mesmo). */
function iniciais(nome: string): string {
  return nome
    .split(' ')
    .map((parte) => parte[0] ?? '')
    .slice(0, 2)
    .join('')
    .toUpperCase()
}

export function AdminPage() {
  const navigate = useNavigate()
  const { section: sectionId } = useParams()
  const toast = useToast()
  const { search } = useExplorer()

  const section = findAdminSection(sectionId)

  // Filtros do conteudo (a busca do explorer filtra a LISTA de cadastros).
  const [rowSearch, setRowSearch] = useState('')
  const [situation, setSituation] = useState<SituationFilter>('all')
  const [auditQuery, setAuditQuery] = useState('')

  // Estado local dos toggles — a tela responde de verdade sem persistir nada.
  const [situacaoLocal, setSituacaoLocal] = useState<Record<string, boolean>>({})
  const [notificarLocal, setNotificarLocal] = useState<Record<string, boolean>>({})
  const [dominiosExtras, setDominiosExtras] = useState<Record<string, readonly string[]>>({})

  // Modal visual de criar/editar + confirmacao de exclusao + novo dominio.
  const [editando, setEditando] = useState<Editando | null>(null)
  const [form, setForm] = useState<Record<string, string>>({})
  const [dates, setDates] = useState<Record<string, Date | null>>({})
  const [excluindo, setExcluindo] = useState<{ id: string; nome: string } | null>(null)
  const [novoDominioDe, setNovoDominioDe] = useState<CompanyRow | null>(null)
  const [novoDominio, setNovoDominio] = useState('')
  // "+" do explorer nas secoes reais: incrementa e a tela correspondente abre o editor
  const [sinalCriarToggle, setSinalCriarToggle] = useState(0)
  const [sinalCriarPlano, setSinalCriarPlano] = useState(0)
  const [sinalCriarAcesso, setSinalCriarAcesso] = useState(0)
  const [sinalCriarArea, setSinalCriarArea] = useState(0)
  /** Sinal por id de seção para os cadastros reais novos (SPEC-20260815-0821). */
  const [sinaisCriar, setSinaisCriar] = useState<Record<string, number>>({})

  // Trocar de cadastro zera os filtros: eles sao do cadastro anterior.
  useEffect(() => {
    setRowSearch('')
    setSituation('all')
    setAuditQuery('')
  }, [sectionId])

  // Telas fora do plano do workspace somem do explorer (SPEC-20260814-1427).
  const { adminSectionEnabled } = useModuleGates()

  // grupo "Controle de acesso" do explorer: abre sempre RECOLHIDO (pedido
  // do usuario 2026-08-25)
  const [controleAcessoAberto, setControleAcessoAberto] = useState(false)

  const secoesVisiveis = useMemo(() => {
    const contratadas = ADMIN_SECTIONS.filter((item) => adminSectionEnabled(item.id))
    const consulta = search.trim().toLowerCase()
    if (consulta === '') return contratadas
    return contratadas.filter(
      (item) => item.label.toLowerCase().includes(consulta) || item.meta.toLowerCase().includes(consulta),
    )
  }, [search, adminSectionEnabled])

  const estaAtivo = (id: string, padrao: boolean) => situacaoLocal[id] ?? padrao
  const estaNotificando = (row: CompanyRow) => notificarLocal[row.id] ?? row.notify
  const dominiosDe = (row: CompanyRow): readonly string[] => [...row.domains, ...(dominiosExtras[row.id] ?? [])]

  const alternarAtivo = (id: string, padrao: boolean, nome: string) => {
    const proximo = !estaAtivo(id, padrao)
    setSituacaoLocal((atual) => ({ ...atual, [id]: proximo }))
    toast.push({
      tone: 'info',
      message: `“${nome}” ficou ${proximo ? 'ativo' : 'inativo'} nesta sessão — tela de demonstração, nada foi gravado.`,
    })
  }

  const alternarNotificacao = (row: CompanyRow) => {
    const proximo = !estaNotificando(row)
    setNotificarLocal((atual) => ({ ...atual, [row.id]: proximo }))
    toast.push({
      tone: 'info',
      message: proximo
        ? `E-mails de ${row.name} passam a gerar notificação nesta sessão.`
        : `E-mails de ${row.name} deixam de notificar nesta sessão.`,
    })
  }

  const abrirEditor = (alvo: { id: string; name: string; translation?: string } | null) => {
    if (!section || section.kind === 'audit' || section.kind === 'other') return
    const proximoForm: Record<string, string> = {}
    const proximasDatas: Record<string, Date | null> = {}
    for (const field of section.formFields) {
      if (field.kind === 'date') proximasDatas[field.key] = null
      else if (field.kind === 'color') proximoForm[field.key] = COLOR_CHOICES[0]?.value ?? ''
      else if (field.kind === 'select') proximoForm[field.key] = field.options?.[0]?.value ?? ''
      else if (field.key === 'name') proximoForm[field.key] = alvo?.name ?? ''
      else if (field.key === 'translation') proximoForm[field.key] = alvo?.translation ?? ''
      else proximoForm[field.key] = ''
    }

    setForm(proximoForm)
    setDates(proximasDatas)
    setEditando({ id: alvo?.id ?? null, nome: alvo?.name ?? '' })
  }

  // O botao "+" do explorer cria na secao aberta. Auditoria e registro
  // imutavel: nao ha inclusao manual (protótipo `showAdmin.html:94`).
  // Plugins: o "+" precisa saber quem clicou (só o super admin cadastra).
  const sessao = useSession()

  const aoCriarPeloExplorer = () => {
    if (!section) {
      navigate(`/admin/${ADMIN_SECTIONS[0]?.id ?? 'members'}`)
      return
    }
    if (section.kind === 'audit') {
      toast.push({ tone: 'info', message: 'A auditoria é um registro imutável — não existe inclusão manual.' })
      return
    }
    if (section.id === 'feature-toggles') {
      // a tela real de toggles abre o proprio editor ao receber o sinal
      setSinalCriarToggle((s) => s + 1)
      return
    }
    if (section.id === 'plans') {
      setSinalCriarPlano((s) => s + 1)
      return
    }
    if (section.id === 'access-profiles' || section.id === 'access-groups' || section.id === 'members') {
      setSinalCriarAcesso((s) => s + 1)
      return
    }
    if (section.id === 'areas') {
      setSinalCriarArea((s) => s + 1)
      return
    }
    if (['work-item-types', 'work-item-statuses', 'priorities', 'relation-types', 'companies', 'flows', 'custom-fields', 'templates'].includes(section.id)) {
      setSinaisCriar((s) => ({ ...s, [section.id]: (s[section.id] ?? 0) + 1 }))
      return
    }
    /*
     * Plugin não se "adiciona" aqui — e dizer "ainda não aceita inclusão"
     * mandava a pessoa procurar o que não existe. Quem cadastra o que pode
     * existir na instalação é o Console; aqui só se INSTALA o que já está
     * cadastrado. O "+" leva para lá em vez de dar uma desculpa.
     */
    // Conectores de IA: liberar é marcar a pessoa na própria lista
    if (section.id === 'ai-connectors') {
      toast.push({ tone: 'info', message: 'Marque a pessoa na lista para liberar o Orbind pelo Claude.' })
      return
    }
    if (section.id === 'herdar-responsavel') {
      toast.push({ tone: 'info', message: 'Use "Ver o que mudaria" na própria tela.' })
      return
    }
    if (section.id === 'plugins') {
      if (sessao.isSuperAdmin) {
        navigate('/console/plugins')
      } else {
        toast.push({
          tone: 'info',
          message: 'Plugins são cadastrados no Console, pelo super admin da instalação. Aqui você instala os que já estiverem cadastrados.',
        })
      }
      return
    }
    if (section.kind === 'other') {
      toast.push({ tone: 'info', message: `${section.label} ainda não aceita inclusão nesta rodada.` })
      return
    }
    abrirEditor(null)
  }

  useExplorerConfig({
    footer:
      secoesVisiveis.length === ADMIN_SECTIONS.length
        ? `${ADMIN_SECTIONS.length} cadastros configuráveis da plataforma`
        : `${secoesVisiveis.length} de ${ADMIN_SECTIONS.length} cadastros`,
    onCreate: aoCriarPeloExplorer,
  })

  const salvarMock = () => {
    setEditando(null)
    toast.push({
      tone: 'info',
      message: 'Tela de demonstração: os cadastros ainda usam dados de exemplo e nada foi gravado.',
    })
  }

  const busca = rowSearch.trim().toLowerCase()
  const passaSituacao = (ativo: boolean) =>
    situation === 'all' || (situation === 'active' ? ativo : !ativo)

  // ------------------------------------------------------------ explorer ---

  // "Controle de acesso" agrupa Membros, Grupos de usuários e Perfis como
  // SUBMENUS (pedido 2026-08-25); a ordem aqui e a do menu.
  const GRUPO_CONTROLE_ACESSO = ['members', 'access-groups', 'access-profiles']
  const secoesDoControle = GRUPO_CONTROLE_ACESSO.map((sid) => secoesVisiveis.find((s) => s.id === sid)).filter(
    (s): s is (typeof secoesVisiveis)[number] => s !== undefined,
  )
  const secoesForaDoControle = secoesVisiveis.filter((s) => !GRUPO_CONTROLE_ACESSO.includes(s.id))
  const controleTemAtiva = GRUPO_CONTROLE_ACESSO.includes(section?.id ?? '')
  const buscando = search.trim() !== ''
  // o CLIQUE no grupo sempre manda (recolhe mesmo com tela ativa dentro);
  // so a busca forca aberto para os resultados aparecerem
  const mostrarSubmenus = controleAcessoAberto || buscando

  const linhaDaSecao = (item: (typeof secoesVisiveis)[number], sub = false) => (
    <button
      key={item.id}
      type="button"
      className={`adm-secrow${sub ? ' adm-secrow--sub' : ''}${item.id === section?.id ? ' adm-secrow--on' : ''}`}
      aria-current={item.id === section?.id ? 'page' : undefined}
      onClick={() => navigate(`/admin/${item.id}`)}
    >
      <span className="adm-secrow__icon">
        <i className={item.icon} aria-hidden />
      </span>
      <span className="adm-secrow__main">
        <span className="adm-secrow__label">{item.label}</span>
        <span className="adm-secrow__meta">{item.meta}</span>
      </span>
      <i className="fas fa-chevron-right adm-secrow__chevron" aria-hidden />
    </button>
  )

  const listaDoExplorer = (
    <div className="adm-seclist">
      {secoesDoControle.length > 0 ? (
        <>
          <button
            type="button"
            className={`adm-secrow adm-secrow--grupo${controleTemAtiva ? ' adm-secrow--on' : ''}`}
            aria-expanded={mostrarSubmenus}
            onClick={() => setControleAcessoAberto((v) => !v)}
          >
            <span className="adm-secrow__icon">
              <i className="fas fa-user-shield" aria-hidden />
            </span>
            <span className="adm-secrow__main">
              <span className="adm-secrow__label">Controle de acesso</span>
              <span className="adm-secrow__meta">Membros · grupos de usuários · perfis</span>
            </span>
            <i className={`fas fa-chevron-${mostrarSubmenus ? 'down' : 'right'} adm-secrow__chevron`} aria-hidden />
          </button>
          {mostrarSubmenus ? secoesDoControle.map((item) => linhaDaSecao(item, true)) : null}
        </>
      ) : null}

      {secoesForaDoControle.map((item) => linhaDaSecao(item))}

      {secoesVisiveis.length === 0 ? (
        <div className="adm-secempty">
          <i className="fas fa-search" aria-hidden />
          <span className="adm-secempty__title">Nenhum cadastro encontrado</span>
          <span className="adm-secempty__text">Nenhum cadastro casa com “{search}”.</span>
        </div>
      ) : null}
    </div>
  )

  // ----------------------------------------------------------- cabecalhos ---

  const cabecalho = (titulo: string, descricao: string, acao?: ReactNode) => (
    <div className="adm-head">
      <div>
        <h1 className="adm-head__title">{titulo}</h1>
        <p className="adm-head__desc">{descricao}</p>
      </div>
      {acao}
    </div>
  )

  const barraDeFiltros = (rotulo: string, filtradas: number, total: number) => (
    <div className="adm-toolbar">
      <div className="adm-toolbar__search">
        <i className="fas fa-search" aria-hidden />
        <input
          type="search"
          placeholder={`Filtrar em ${rotulo.toLowerCase()}…`}
          aria-label={`Filtrar registros de ${rotulo}`}
          value={rowSearch}
          onChange={(event) => setRowSearch(event.target.value)}
        />
      </div>
      <div className="adm-toolbar__situation">
        {/* rotulo so para leitor de tela: o gatilho ja mostra o valor */}
        <span id="adm-situation-label" className="ob-sr-only">
          Filtrar por situação
        </span>
        <Select<SituationFilter>
          value={situation}
          options={SITUATION_OPTIONS}
          onChange={setSituation}
          aria-labelledby="adm-situation-label"
        />
      </div>
      <span className="adm-toolbar__count">
        {filtradas} de {total} {total === 1 ? 'registro' : 'registros'}
      </span>
    </div>
  )

  const semResultado = (sec: AdminSection, vazioDeVerdade: boolean) => (
    <div className="adm-placeholder">
      <EmptyState
        icon={sec.icon}
        title={vazioDeVerdade ? `Nenhum registro em ${sec.label}` : 'Nenhum registro com esses filtros'}
        description={
          vazioDeVerdade
            ? sec.emptyDescription
            : 'Ajuste a busca ou volte para todas as situações para ver os registros deste cadastro.'
        }
        action={
          vazioDeVerdade ? (
            <Button variant="primary" icon="fas fa-plus" onClick={() => abrirEditor(null)}>
              {sec.createLabel}
            </Button>
          ) : (
            <Button
              variant="secondary"
              icon="fas fa-times"
              onClick={() => {
                setRowSearch('')
                setSituation('all')
              }}
            >
              Limpar filtros
            </Button>
          )
        }
      />
    </div>
  )

  // ------------------------------------------------------- membros ---

  const renderMembros = (sec: Secao<'members'>) => {
    const linhas = sec.rows.filter(
      (row) =>
        passaSituacao(estaAtivo(row.id, row.isActive)) &&
        (busca === '' || `${row.name} ${row.email} ${row.area} ${row.presence.label}`.toLowerCase().includes(busca)),
    )

    const integracoesEmTexto = (row: MemberRow) => {
      const partes: string[] = []
      if (row.google) partes.push('Google conectado')
      if (row.mailbox) partes.push('Gmail integrado')
      return partes.length === 0 ? 'Nenhuma integração conectada' : partes.join(' e ')
    }

    return (
      <>
        {cabecalho(
          sec.title,
          sec.description,
          <Button variant="primary" icon="fas fa-plus" onClick={() => abrirEditor(null)}>
            {sec.createLabel}
          </Button>,
        )}
        {barraDeFiltros(sec.label, linhas.length, sec.rows.length)}

        {linhas.length === 0 ? (
          semResultado(sec, sec.rows.length === 0)
        ) : (
          <div className="adm-table">
            <div className="adm-table__scroll">
              <div className="adm-table__inner adm-table__inner--collab">
                <div className="adm-table__head adm-grid--collab" role="presentation">
                  <span>Nome</span>
                  <span>E-mail</span>
                  <span>Área</span>
                  <span>Presença</span>
                  <span>Integrações</span>
                </div>

                {linhas.map((row) => {
                  const ativo = estaAtivo(row.id, row.isActive)
                  return (
                    <button
                      key={row.id}
                      type="button"
                      className={`adm-table__row adm-grid--collab${ativo ? '' : ' adm-table__row--off'}`}
                      title="Clique para editar"
                      onClick={() => abrirEditor(row)}
                    >
                      <span className="adm-person">
                        <span className="adm-av">
                          <span className="adm-av__ini" aria-hidden>
                            {row.initials}
                          </span>
                          {/* dot de presenca: apoio visual — a coluna Presença
                              traz o rotulo por escrito */}
                          <span className="adm-av__dot" style={{ background: row.presence.color }} aria-hidden />
                        </span>
                        <span className="adm-person__name">{row.name}</span>
                        {!ativo ? <span className="adm-sys">Inativo</span> : null}
                      </span>

                      <span className="adm-cell">{row.email}</span>
                      <span className="adm-cell">
                        <span className="adm-areatag">{row.area}</span>
                      </span>
                      <span className="adm-cell adm-presence">
                        <span className="adm-presence__dot" style={{ background: row.presence.color }} aria-hidden />
                        {row.presence.label}
                      </span>

                      <span className="adm-int">
                        <i
                          className={`fab fa-google adm-int__icon${row.google ? '' : ' adm-int__icon--off'}`}
                          title={row.google ? 'Google conectado' : 'Google não conectado'}
                          aria-hidden
                        />
                        <i
                          className={`far fa-envelope adm-int__icon${row.mailbox ? '' : ' adm-int__icon--off'}`}
                          title={row.mailbox ? 'Gmail integrado' : 'Gmail não integrado'}
                          aria-hidden
                        />
                        <span className="ob-sr-only">{integracoesEmTexto(row)}</span>
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>
          </div>
        )}
      </>
    )
  }

  // -------------------------------------------------------------- empresas ---

  const renderEmpresas = (sec: Secao<'companies'>) => {
    const linhas = sec.rows.filter(
      (row) =>
        passaSituacao(estaAtivo(row.id, row.isActive)) &&
        (busca === '' ||
          `${row.name} ${row.code} ${row.owner} ${dominiosDe(row).join(' ')}`.toLowerCase().includes(busca)),
    )

    return (
      <>
        {cabecalho(
          sec.title,
          sec.description,
          <Button variant="primary" icon="fas fa-plus" onClick={() => abrirEditor(null)}>
            {sec.createLabel}
          </Button>,
        )}
        {barraDeFiltros(sec.label, linhas.length, sec.rows.length)}

        {linhas.length === 0 ? (
          semResultado(sec, sec.rows.length === 0)
        ) : (
          <div className="adm-cards">
            {linhas.map((row) => {
              const ativo = estaAtivo(row.id, row.isActive)
              const notificando = estaNotificando(row)
              return (
                <div key={row.id} className={`adm-client${ativo ? '' : ' adm-client--off'}`}>
                  <div className="adm-client__head">
                    <span className="adm-client__icon">
                      <i className="fas fa-building" aria-hidden />
                    </span>
                    <span className="adm-client__id">
                      <span className="adm-client__name">{row.name}</span>
                      <span className="adm-client__meta">
                        {row.code} · Responsável: {row.owner} · {row.counts}
                      </span>
                    </span>

                    {!ativo ? (
                      <Badge tone="neutral" icon="fas fa-pause-circle">
                        Inativo
                      </Badge>
                    ) : null}

                    <span className="adm-switchwrap">
                      <Interruptor
                        ligado={notificando}
                        rotulo={`Notificar e-mails de ${row.name}`}
                        titulo={
                          notificando
                            ? 'Notificando — clique para parar de notificar'
                            : 'Sem notificação — clique para passar a notificar'
                        }
                        aoAlternar={() => alternarNotificacao(row)}
                      />
                      <span className="adm-switchwrap__label">Notificar e-mails</span>
                    </span>

                    <button
                      type="button"
                      className="adm-iconbtn adm-iconbtn--outline"
                      aria-label={`Editar ${row.name}`}
                      title="Editar empresa"
                      onClick={() => abrirEditor(row)}
                    >
                      <i className="fas fa-pen" aria-hidden />
                    </button>
                  </div>

                  <div className="adm-client__domains">
                    <span className="adm-client__domlabel">Domínios correlacionados:</span>
                    {dominiosDe(row).map((dominio) => (
                      <span key={dominio} className="adm-dom">
                        @{dominio}
                      </span>
                    ))}
                    <button
                      type="button"
                      className="adm-adddom"
                      title={`Acrescentar domínio a ${row.name}`}
                      onClick={() => {
                        setNovoDominio('')
                        setNovoDominioDe(row)
                      }}
                    >
                      + domínio
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </>
    )
  }

  // ------------------------------------------------------------- catalogos ---

  const renderCatalogo = (sec: Secao<'catalog'>) => {
    const linhas = sec.rows.filter(
      (row) =>
        passaSituacao(estaAtivo(row.id, row.isActive)) &&
        (busca === '' || `${row.name} ${row.translation} ${row.extra ?? ''}`.toLowerCase().includes(busca)),
    )

    const legenda = (row: CatalogRow) => {
      const traducao = `${sec.secondaryLabel}: ${row.translation || '—'}`
      return row.extra ? `${traducao} · ${row.extra}` : traducao
    }

    return (
      <>
        {cabecalho(
          sec.title,
          sec.description,
          <Button variant="primary" icon="fas fa-plus" onClick={() => abrirEditor(null)}>
            Incluir
          </Button>,
        )}
        {barraDeFiltros(sec.label, linhas.length, sec.rows.length)}

        {linhas.length === 0 ? (
          semResultado(sec, sec.rows.length === 0)
        ) : (
          <div className="adm-list">
            {linhas.map((row) => {
              const ativo = estaAtivo(row.id, row.isActive)
              return (
                <div key={row.id} className={`adm-catrow${ativo ? '' : ' adm-catrow--off'}`}>
                  {row.icon ? (
                    // a cor escolhida no cadastro pinta o icone aqui — e o
                    // retorno visual de que a edicao valeu
                    // a cor do cadastro pinta o proprio icone — a bolinha
                    // separada saiu. O nome da cor vai no `title`, porque cor
                    // nunca e o unico sinal (o nome e a legenda continuam).
                    <span
                      className="adm-catrow__icon"
                      style={row.dot ? { color: row.dot.color } : undefined}
                      title={row.dot ? `Cor: ${row.dot.label}` : undefined}
                    >
                      <CatalogIcon icon={row.icon} />
                    </span>
                  ) : null}

                  <span className="adm-catrow__main">
                    <span className="adm-catrow__name">{row.name}</span>
                    <span className="adm-catrow__sub">{legenda(row)}</span>
                  </span>

                  {row.badge ? (
                    <Badge tone={row.badge.tone} icon={row.badge.icon}>
                      {row.badge.label}
                    </Badge>
                  ) : null}
                  {row.isSystem ? <span className="adm-sys">Sistema</span> : null}

                  <span className="adm-switchwrap">
                    <span className="adm-switchwrap__label">{ativo ? 'Ativo' : 'Inativo'}</span>
                    <Interruptor
                      ligado={ativo}
                      rotulo={`${row.name} está ativo`}
                      titulo={ativo ? 'Ativo — clique para desativar' : 'Inativo — clique para ativar'}
                      aoAlternar={() => alternarAtivo(row.id, row.isActive, row.name)}
                    />
                  </span>

                  <button
                    type="button"
                    className="adm-iconbtn"
                    aria-label={`Editar ${row.name}`}
                    title="Editar"
                    onClick={() => abrirEditor(row)}
                  >
                    <i className="fas fa-pen" aria-hidden />
                  </button>
                  <button
                    type="button"
                    className="adm-iconbtn adm-iconbtn--danger"
                    disabled={row.isSystem}
                    aria-label={
                      row.isSystem ? `${row.name} é do sistema e não pode ser excluído` : `Excluir ${row.name}`
                    }
                    title={
                      row.isSystem
                        ? 'Registro do sistema: não pode ser excluído — desative-o se necessário'
                        : 'Excluir'
                    }
                    onClick={() => setExcluindo({ id: row.id, nome: row.name })}
                  >
                    <i className="fas fa-trash-alt" aria-hidden />
                  </button>
                </div>
              )
            })}
          </div>
        )}
      </>
    )
  }

  // ------------------------------------------------------------- auditoria ---

  const renderAuditoria = (sec: Secao<'audit'>) => {
    const consulta = auditQuery.trim().toLowerCase()
    const linhas = sec.entries.filter(
      (entry) => consulta === '' || `${entry.who} ${entry.what} ${entry.where}`.toLowerCase().includes(consulta),
    )

    return (
      <>
        {cabecalho(
          sec.title,
          sec.description,
          <div className="adm-audfilter">
            <i className="fas fa-search" aria-hidden />
            <input
              type="search"
              placeholder="Filtrar por pessoa, ação ou área…"
              aria-label="Filtrar a auditoria por pessoa, ação ou área"
              value={auditQuery}
              onChange={(event) => setAuditQuery(event.target.value)}
            />
          </div>,
        )}

        {linhas.length === 0 ? (
          <div className="adm-placeholder">
            <EmptyState
              icon="fas fa-history"
              title={sec.entries.length === 0 ? 'Nenhuma alteração registrada' : 'Nenhuma alteração com esse filtro'}
              description={
                sec.entries.length === 0
                  ? sec.emptyDescription
                  : `Nada na auditoria casa com “${auditQuery}”. Tente o nome de uma pessoa, uma ação ou uma área.`
              }
              action={
                sec.entries.length === 0 ? undefined : (
                  <Button variant="secondary" icon="fas fa-times" onClick={() => setAuditQuery('')}>
                    Limpar o filtro
                  </Button>
                )
              }
            />
          </div>
        ) : (
          <div className="adm-list">
            {linhas.map((entry) => {
              const automacao = entry.who === AUDIT_AUTOMACAO
              return (
                <div key={entry.id} className="adm-audrow">
                  <span className={`adm-audav${automacao ? ' adm-audav--bot' : ''}`} aria-hidden>
                    {automacao ? <i className="fas fa-bolt" /> : iniciais(entry.who)}
                  </span>
                  <span className="adm-audrow__main">
                    <span className="adm-audrow__what">{entry.what}</span>
                    <span className="adm-audrow__who">
                      {entry.who} · {entry.where}
                    </span>
                  </span>
                  <span className="adm-audrow__time">{entry.time}</span>
                </div>
              )
            })}
          </div>
        )}
      </>
    )
  }

  // ------------------------------------------------- cartao "ainda nao vem" ---

  const cartaoOutro = (titulo: string) => (
    <div className="adm-placeholder">
      <EmptyState
        icon="fas fa-cog"
        title={titulo}
        description="Este cadastro segue o mesmo padrão configurável (código, traduções, ordem, situação, uso e histórico) e será detalhado na próxima rodada."
        action={
          <Button variant="primary" icon="fas fa-list" onClick={() => navigate('/admin')}>
            Ver os cadastros disponíveis
          </Button>
        }
      />
    </div>
  )

  const conteudo = () => {
    if (!section) {
      if (sectionId) {
        // rota /admin/<algo-que-nao-existe>: mesmo cartao `admOther` do
        // protótipo (`showAdmin.html:115`), em vez de um 404 seco
        return cartaoOutro('Cadastro ainda não disponível')
      }
      return (
        <div className="adm-placeholder">
          <EmptyState
            icon="fas fa-cog"
            title="Selecione um cadastro"
            description="Escolha um cadastro no explorer para ver e editar os registros da instalação — membros, empresas, áreas, catálogos, idiomas, integrações e a auditoria."
            action={
              <Button
                variant="primary"
                icon="fas fa-user-friends"
                onClick={() => navigate(`/admin/${ADMIN_SECTIONS[0]?.id ?? 'members'}`)}
              >
                Começar pelos Membros
              </Button>
            }
          />
        </div>
      )
    }

    // Tela fora do plano: URL direta cai no bloqueio (SPEC-20260814-1427).
    if (!adminSectionEnabled(section.id)) {
      return (
        <>
          {cabecalho(section.title, section.description)}
          <AdminSectionBlocked label={section.label} />
        </>
      )
    }

    // Feature toggles e secao REAL com tela propria (SPEC-20260814-1144); o
    // registro em ADMIN_SECTIONS (kind 'other') so a poe no explorer.
    if (section.id === 'feature-toggles') return <FeatureTogglesSection criarSinal={sinalCriarToggle} />
    if (section.id === 'plans') return <PlansSection criarSinal={sinalCriarPlano} />
    // Controle de acesso (SPEC-20260814-1444): telas reais; Membros
    // deixa de ser a secao mocada.
    // Areas: primeira secao no padrao SEM MODAL (SPEC-20260814-2334)
    if (section.id === 'areas') return <AreasSection criarSinal={sinalCriarArea} />
    if (section.id === 'access-profiles') return <AccessProfilesSection criarSinal={sinalCriarAcesso} />
    if (section.id === 'access-groups') return <AccessGroupsSection criarSinal={sinalCriarAcesso} />
    if (section.id === 'members') return <AccessMembersSection criarSinal={sinalCriarAcesso} />
    // Cadastros reais no padrao FormScreen (SPEC-20260815-0821)
    if (section.id === 'work-item-types') return <WorkItemTypesSection criarSinal={sinaisCriar['work-item-types'] ?? 0} />
    if (section.id === 'work-item-statuses') return <StatusesSection criarSinal={sinaisCriar['work-item-statuses'] ?? 0} />
    if (section.id === 'priorities') return <PrioritiesSection criarSinal={sinaisCriar['priorities'] ?? 0} />
    if (section.id === 'relation-types') return <RelationTypesSection criarSinal={sinaisCriar['relation-types'] ?? 0} />
    if (section.id === 'companies') return <CompaniesSection criarSinal={sinaisCriar['companies'] ?? 0} />
    if (section.id === 'flows') return <FlowsSection criarSinal={sinaisCriar['flows'] ?? 0} />
    if (section.id === 'templates') return <TemplatesSection criarSinal={sinaisCriar['templates'] ?? 0} />
    if (section.id === 'plugins') return <PluginsSection />
    if (section.id === 'ai-connectors') return <ConectoresDeIaSection />
    if (section.id === 'herdar-responsavel') return <HerdarResponsavelSection />
    if (section.id === 'custom-fields')
      return <CamposComplementaresSection criarSinal={sinaisCriar['custom-fields'] ?? 0} />

    switch (section.kind) {
      case 'members':
        return renderMembros(section)
      case 'companies':
        return renderEmpresas(section)
      case 'catalog':
        return renderCatalogo(section)
      case 'audit':
        return renderAuditoria(section)
      case 'other':
        return (
          <>
            {cabecalho(section.title, section.description)}
            {cartaoOutro(section.label)}
          </>
        )
    }
  }

  const formFields = section && section.kind !== 'audit' && section.kind !== 'other' ? section.formFields : []
  // Salvar so libera com nome preenchido — protótipo `_shell.html:3663`
  // (`catEdSaveStyle` alterna para "cursor:not-allowed" quando `f1` esta vazio).
  const nomePreenchido = (form.name ?? '').trim() !== ''

  return (
    <div className="adm-page">
      <ExplorerBody>{listaDoExplorer}</ExplorerBody>

      {conteudo()}

      {/* modal visual de criar/editar — nao persiste (dados mocados) */}
      <Modal
        open={editando !== null && formFields.length > 0}
        onClose={() => setEditando(null)}
        title={
          section === null ? '' : editando?.id ? `Editar · ${editando.nome}` : `Incluir em ${section.label}`
        }
        width={520}
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditando(null)}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              disabled={!nomePreenchido}
              title={nomePreenchido ? undefined : 'Informe o nome para poder salvar'}
              onClick={salvarMock}
            >
              Salvar
            </Button>
          </>
        }
      >
        <div className="adm-form">
          {formFields.map((field) => {
            if (field.kind === 'textarea') {
              return (
                <FieldBlock key={field.key} label={field.label} required={field.required} hint={field.hint}>
                  {({ id }) => (
                    <Textarea
                      id={id}
                      rows={3}
                      placeholder={field.placeholder}
                      value={form[field.key] ?? ''}
                      onChange={(event) => setForm((atual) => ({ ...atual, [field.key]: event.target.value }))}
                    />
                  )}
                </FieldBlock>
              )
            }
            if (field.kind === 'select') {
              return (
                <FieldBlock key={field.key} label={field.label} required={field.required} hint={field.hint}>
                  {({ id }) => (
                    <Select
                      id={id}
                      value={form[field.key] ?? null}
                      options={(field.options ?? []).map((option) => ({ value: option.value, label: option.label }))}
                      onChange={(value) => setForm((atual) => ({ ...atual, [field.key]: value }))}
                    />
                  )}
                </FieldBlock>
              )
            }
            if (field.kind === 'date') {
              return (
                <FieldBlock key={field.key} label={field.label} required={field.required} hint={field.hint}>
                  {({ id }) => (
                    <DateInput
                      id={id}
                      value={dates[field.key] ?? null}
                      onChange={(value) => setDates((atual) => ({ ...atual, [field.key]: value }))}
                    />
                  )}
                </FieldBlock>
              )
            }
            // O icone tem seletor proprio: biblioteca do Font Awesome, envio
            // de arquivo ou endereco na internet.
            if (field.key === 'icon') {
              return (
                <IconField
                  key={field.key}
                  label={field.label}
                  value={form[field.key] ?? ''}
                  imageId={form.iconImageId || null}
                  color={form.color || undefined}
                  onChange={(escolha) =>
                    setForm((atual) => ({
                      ...atual,
                      icon: escolha.icon,
                      iconImageId: escolha.iconImageId ?? '',
                    }))
                  }
                />
              )
            }
            if (field.kind === 'color') {
              const selecionada = form[field.key] ?? ''
              const nomeDaCor = COLOR_CHOICES.find((color) => color.value === selecionada)?.label ?? 'Nenhuma'
              return (
                <FieldBlock
                  key={field.key}
                  label={field.label}
                  hint={field.hint ?? 'A cor é apoio visual: o rótulo continua sendo o que comunica o significado.'}
                >
                  {() => (
                    <div className="adm-colors">
                      {COLOR_CHOICES.map((color) => {
                        const on = color.value === selecionada
                        return (
                          <button
                            key={color.value}
                            type="button"
                            className={`adm-color${on ? ' adm-color--on' : ''}`}
                            style={{ background: color.value }}
                            aria-pressed={on}
                            aria-label={color.label}
                            title={color.label}
                            onClick={() => setForm((atual) => ({ ...atual, [field.key]: color.value }))}
                          >
                            {on ? <i className="fas fa-check" aria-hidden /> : null}
                          </button>
                        )
                      })}
                      <span className="adm-colors__label">Cor escolhida: {nomeDaCor}</span>
                    </div>
                  )}
                </FieldBlock>
              )
            }
            return (
              <FieldBlock key={field.key} label={field.label} required={field.required} hint={field.hint}>
                {({ id }) => (
                  <Input
                    id={id}
                    autoFocus={field.key === 'name'}
                    placeholder={field.placeholder}
                    value={form[field.key] ?? ''}
                    onChange={(event) => setForm((atual) => ({ ...atual, [field.key]: event.target.value }))}
                  />
                )}
              </FieldBlock>
            )
          })}
        </div>
      </Modal>

      {/* modal do "+ domínio" do cartao de empresa */}
      <Modal
        open={novoDominioDe !== null}
        onClose={() => setNovoDominioDe(null)}
        title={novoDominioDe ? `Novo domínio · ${novoDominioDe.name}` : ''}
        width={440}
        footer={
          <>
            <Button variant="ghost" onClick={() => setNovoDominioDe(null)}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              disabled={novoDominio.trim() === ''}
              title={novoDominio.trim() === '' ? 'Informe o domínio para poder salvar' : undefined}
              onClick={() => {
                const alvo = novoDominioDe
                const dominio = novoDominio.trim().replace(/^@/, '')
                if (!alvo || dominio === '') return
                setDominiosExtras((atual) => ({ ...atual, [alvo.id]: [...(atual[alvo.id] ?? []), dominio] }))
                setNovoDominioDe(null)
                toast.push({
                  tone: 'info',
                  message: `@${dominio} passa a correlacionar e-mails com ${alvo.name} nesta sessão — nada foi gravado.`,
                })
              }}
            >
              Salvar
            </Button>
          </>
        }
      >
        <div className="adm-form">
          <FieldBlock
            label="Domínio de e-mail"
            required
            hint="Todo e-mail recebido deste domínio passa a ser correlacionado com este cliente."
          >
            {({ id }) => (
              <Input
                id={id}
                autoFocus
                placeholder="empresa.com.br"
                value={novoDominio}
                onChange={(event) => setNovoDominio(event.target.value)}
              />
            )}
          </FieldBlock>
        </div>
      </Modal>

      <ConfirmDialog
        open={excluindo !== null}
        title="Excluir registro"
        description={
          <>
            <strong>{excluindo?.nome}</strong> deixa de aparecer em filtros, quadros e relatórios. Os itens que já usam
            este registro mantêm o histórico.
          </>
        }
        confirmLabel="Excluir"
        destructive
        onCancel={() => setExcluindo(null)}
        onConfirm={() => {
          setExcluindo(null)
          toast.push({ tone: 'info', message: 'Tela de demonstração: nenhum registro foi excluído.' })
        }}
      />
    </div>
  )
}
